import {
  Directive,
  ElementRef,
  HostBinding,
  OnInit,
  inject
} from '@angular/core';

/**
 * Enhanced Gap-aware collapse/expand animation directive.
 *
 * It automatically binds the `@collapseInOut` Angular animation and computes
 * the parent's flex-gap to set a CSS variable `--smooth-collapse-gap`.
 * This approach guarantees that even when elements have paddings (which previously
 * caused the "hard snap"), everything collapses perfectly to 0 pixels.
 *
 * Usage: replace `@collapseInOut` with `smoothCollapse` on the element.
 *
 * @example
 * ```html
 * <div smoothCollapse class="my-section">...</div>
 * ```
 */
@Directive({
  selector: '[smoothCollapse]',
  standalone: true
})
export class SmoothCollapseDirective implements OnInit {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostBinding('@collapseInOut') collapseAnim = true;

  ngOnInit(): void {
    const parent = this.el.nativeElement.parentElement;
    if (!parent) return;

    const styles = getComputedStyle(parent);
    const dir = styles.flexDirection;
    const isRow = dir === 'row' || dir === 'row-reverse';
    const raw = isRow
      ? styles.getPropertyValue('column-gap')
      : styles.getPropertyValue('row-gap');
    const val = parseFloat(raw);

    if (Number.isFinite(val) && val > 0) {
      this.el.nativeElement.style.setProperty(
        '--smooth-collapse-gap',
        `-${val}px`
      );
    }
  }
}
